import React, { useState, useContext } from "react";
import RoleList from "./RoleList";
import CreateRoleForm from "./CreateRoleForm";
import EditRoleForm from "./EditRoleForm";
import { AuthContext } from "../../contexts/AuthContext";

const Roles = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [selectedRole, setSelectedRole] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";

  const handleRoleCreated = () => {
    setActiveTab("list");
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleRoleUpdated = () => {
    setSelectedRole(null);
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <div className="container mt-4">
      <div className="row mb-4">
        <div className="col">
          <h2>Role Management</h2>
          <p className="text-muted">
            Manage system roles and access control.
            {!isAdmin && " (View-only access)"}
          </p>
        </div>
      </div>

      {/* Security Alert */}
      <div
        className="alert alert-info d-flex align-items-center mb-4"
        role="alert"
      >
        <i className="bi bi-shield-lock fs-4 me-3"></i>
        <div>
          <strong>Security Notice:</strong> Roles control system access
          permissions. Changes to roles affect all users assigned to those roles
          across the entire system.
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => {
              setSelectedRole(null);
              setActiveTab("list");
            }}
          >
            <i className="bi bi-list-ul"></i> Role List
          </button>
        </li>
        {isAdmin && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "create" ? "active" : ""}`}
              onClick={() => {
                setSelectedRole(null);
                setActiveTab("create");
              }}
            >
              <i className="bi bi-plus-circle"></i> Create Role
            </button>
          </li>
        )}
      </ul>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "list" && (
          <>
            {!selectedRole ? (
              <RoleList
                onEdit={(role) => {
                  if (isAdmin) {
                    setSelectedRole(role);
                  }
                }}
                refreshTrigger={refreshTrigger}
              />
            ) : (
              <EditRoleForm
                key={selectedRole.role_id}
                role={selectedRole}
                onCancel={() => setSelectedRole(null)}
                onUpdated={handleRoleUpdated}
              />
            )}
          </>
        )}

        {activeTab === "create" && isAdmin && (
          <CreateRoleForm onSuccess={handleRoleCreated} />
        )}
      </div>

      {/* Non-admin info message */}
      {!isAdmin && (
        <div className="alert alert-warning mt-4" role="alert">
          <i className="bi bi-info-circle"></i> You have view-only access to
          role management. Contact an administrator to make changes to roles.
        </div>
      )}

      {/* HIPAA Compliance Footer */}
      <div className="mt-5 p-3 bg-light rounded">
        <h6 className="text-muted">
          <i className="bi bi-shield-check"></i> HIPAA Compliance Information
        </h6>
        <small className="text-muted">
          <ul className="mb-0">
            <li>All role modifications are logged for security audit</li>
            <li>Role changes affect system-wide access control</li>
            <li>System roles (admin, super_admin) cannot be deleted</li>
            <li>Users must be reassigned before roles can be deleted</li>
          </ul>
        </small>
      </div>
    </div>
  );
};

export default Roles;
